import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { API_URL } from "@/shared/constants"
import { useAuth } from "@features/auth/hooks/useAuth.ts"
import { CountryScope, fallbackCountryScope, normalizeCountryScope } from "./country-scope"

export const COUNTRY_SCOPE_KEY = "USERS_ME_COUNTRY"

/**
 * País efectivo del usuario para las pantallas por país (logística, bandejas, guías). La fuente de verdad es
 * `GET /users/me/country` (el `country` del usuario en localStorage puede estar viejo); mientras carga o si falla se
 * asume el país del perfil guardado (sin país = Perú) y que solo el admin o el binacional (EC-19) ven los dos países.
 */
export const useCountryScope = (): CountryScope & { isLoading: boolean } => {
  const { user, hasRole } = useAuth()
  const fallback = fallbackCountryScope(user?.country, hasRole("admin"), user?.binational === true)
  const { data, isLoading } = useQuery({
    // Con el usuario en la llave: al impersonar a otro usuario no se reutiliza el país del anterior
    queryKey: [COUNTRY_SCOPE_KEY, user?.id ?? user?.email ?? ""],
    queryFn: async () => {
      const res = await axios.get<{ data?: Partial<CountryScope> }>(`${API_URL}/users/me/country`)
      return res.data?.data ?? null
    },
    enabled: !!user,
    staleTime: 30 * 60_000,
    retry: 0,
    refetchOnWindowFocus: false
  })

  return { ...normalizeCountryScope(data, fallback), isLoading: !!user && isLoading }
}
